import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Sidebar } from './components/Common';
import ProfileDropdown from './components/ProfileDropdown';
import NotificationBell from './components/NotificationBell';
import StaffPage from './pages/StaffPage';
import DamagedPage from './pages/DamagedPage';

// Shop Admin Pages
import LoginPage      from './pages/LoginPage';
import DashboardPage  from './pages/DashboardPage';
import InventoryPage  from './pages/InventoryPage';
import CustomersPage  from './pages/CustomersPage';
import BillingPage    from './pages/BillingPage';
import InvoicesPage   from './pages/InvoicesPage';
import ReportsPage    from './pages/ReportsPage';

// Super Admin Pages
import SuperAdminLogin         from './pages/SuperAdminLogin';
import SuperAdminDashboard     from './pages/SuperAdminDashboard';
import SuperAdminAdmins        from './pages/SuperAdminAdmins';
import SuperAdminPlans         from './pages/SuperAdminPlans';
import SuperAdminNotifications from './pages/SuperAdminNotifications';
import SuperAdminLeads from './pages/SuperAdminLeads';
import SuperAdminLayout        from './components/SuperAdminLayout';
import PaymentsPage from './pages/PaymentsPage';
import OfflineSalePage from './pages/OfflineSalePage';
import LandingPage from './pages/LandingPage';

import './App.css';

// ── Shop Admin Protected Layout ──────────────────────────────
const ProtectedLayout = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const [showSplash, setShowSplash] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const prevUser = React.useRef(null);

  React.useEffect(() => {
    const justLoggedIn = sessionStorage.getItem('bhamsa_just_logged_in');
    if (user && justLoggedIn) {
      sessionStorage.removeItem('bhamsa_just_logged_in');
      setShowSplash(true);
      setTimeout(() => setShowSplash(false), 5000);
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  if (showSplash) return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#060919',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <img
        src="/bhamsa-loading.jpg"
        alt="Loading"
        style={{
          width: '100%',
          animation: 'fadeIn 0.5s ease',
        }}
      />
    </div>
  );
  return (
    <div className={"app-layout" + (showMenu ? " show-menu" : "")}>
     <Sidebar user={user} onLogout={logout} onLogoClick={() => setShowMenu(false)} />
      <main className="main-content">
        <div className="topbar">
          <div className="topbar-title" onClick={() => setShowMenu(!showMenu)}>{user?.shop_name || 'Shop'}</div>
            <div className="topbar-right">
              <span className='date-day' style={{ fontSize: 12, color: 'var(--text3)' }}>
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long', day: 'numeric', month: 'short'
                })}
              </span>
              <NotificationBell />
              <ProfileDropdown user={user} onUpdate={(updatedUser) => {
                localStorage.setItem('bhamsa_user', JSON.stringify({...user, ...updatedUser}));
              }} />
            </div>
        </div>
        {children}
      </main>
    </div>
  );
};

// ── Super Admin Protected Layout ─────────────────────────────
const SuperAdminProtected = ({ children }) => {
  const token = localStorage.getItem('bhamsa_sa_token');
  if (!token) return <Navigate to="/super-admin/login" replace />;
  return <SuperAdminLayout>{children}</SuperAdminLayout>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ── Super Admin Routes ── */}
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/super-admin/dashboard" element={
            <SuperAdminProtected><SuperAdminDashboard /></SuperAdminProtected>
          } />
          <Route path="/super-admin/admins" element={
            <SuperAdminProtected><SuperAdminAdmins /></SuperAdminProtected>
          } />
          <Route path="/super-admin/plans" element={
            <SuperAdminProtected><SuperAdminPlans /></SuperAdminProtected>
          } />
          <Route path="/super-admin/notifications" element={
            <SuperAdminProtected><SuperAdminNotifications /></SuperAdminProtected>
          } />
          <Route path="/super-admin/leads" element={
            <SuperAdminProtected><SuperAdminLeads /></SuperAdminProtected>
          } />

          {/* ── Shop Admin Routes ── */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={
            <ProtectedLayout><DashboardPage /></ProtectedLayout>
          } />
          <Route path="/inventory" element={
            <ProtectedLayout><InventoryPage /></ProtectedLayout>
          } />
          <Route path="/customers" element={
            <ProtectedLayout><CustomersPage /></ProtectedLayout>
          } />
          <Route path="/billing" element={
            <ProtectedLayout><BillingPage /></ProtectedLayout>
          } />
          <Route path="/invoices" element={
            <ProtectedLayout><InvoicesPage /></ProtectedLayout>
          } />
          <Route path="/payments" element={
            <ProtectedLayout><PaymentsPage /></ProtectedLayout>
          } />
          <Route path="/staff" element={
            <ProtectedLayout><StaffPage /></ProtectedLayout>
          } />
          <Route path="/damaged" element={
            <ProtectedLayout><DamagedPage /></ProtectedLayout>
          } />
          <Route path="/offline-sale" element={
            <ProtectedLayout><OfflineSalePage /></ProtectedLayout>
          } />
          <Route path="/reports" element={
            <ProtectedLayout><ReportsPage /></ProtectedLayout>
          } />

          {/* Default */}
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: 'var(--bg2)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              fontSize: '13px',
            },
            success: { iconTheme: { primary: '#00d4aa', secondary: '#0a1a14' } },
            error:   { iconTheme: { primary: '#ff6b6b', secondary: '#1a0a0a' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;